/**
 * NotificationSettingsSection - секция «Настройка уведомлений».
 *
 * Два блока:
 *  - канал: браузерная Web Push подписка этого устройства (Service Worker /sw.js, хук кабинета аптеки);
 *  - события: о чем присылать уведомления (/api/v2/profile/notifications, пользователь определяется сервером
 *    по сессии, нет выбора - событие включено). Пока подписки на устройстве нет, события показаны выключенными
 *    и заблокированы: других каналов у раздела нет. Сохраненный выбор при этом не меняется.
 * Рассылки пока не фильтруют push по выбранным событиям (PROJECT_NOTES §35) - об этом честно сказано в секции.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Paper, Typography, Switch, Alert, Collapse, Button, CircularProgress } from '@mui/material';
import { NotificationsNoneOutlined, CheckCircle, InfoOutlined, LockOutlined } from '@mui/icons-material';
import { ZERDE_COLORS } from '../constants';
import SectionHeaderDivider from './SectionHeaderDivider';
import { usePushNotifications } from '../../user/Documents/usePushNotifications';
import { notify } from '../../notifications';
import { apiErrorMessage } from '../../admin/reports/edit/editApi';
import { fetchNotificationEvents, saveNotificationEvents, NotificationEvent } from '../profileApi';

const switchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: ZERDE_COLORS.primary },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ZERDE_COLORS.primary }
};

const statusText = (text: string, color: string) => (
  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color, lineHeight: 1 }}>{text}</Typography>
);

const NotificationSettingsSection: React.FC = () => {
  const [events, setEvents] = useState<NotificationEvent[]>([]);
  const [choice, setChoice] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [infoExpanded, setInfoExpanded] = useState(false);

  const { isSupported, isSubscribed, isLoading, error, permission, subscribe, unsubscribe } = usePushNotifications();

  const applyEvents = (list: NotificationEvent[]) => {
    const next: Record<string, boolean> = {};
    list.forEach(event => {
      next[event.id] = event.enabled;
    });
    setEvents(list);
    setChoice(next);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      applyEvents(await fetchNotificationEvents());
      setLoadError(null);
    } catch (err) {
      setLoadError(apiErrorMessage(err, 'Не удалось загрузить настройки уведомлений'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = events.some(event => choice[event.id] !== event.enabled);

  const handleSave = async () => {
    setSaving(true);
    try {
      const message = await saveNotificationEvents(choice);
      if (message === 'Изменений нет') notify.info(message);
      else notify.success(message);
      applyEvents(events.map(event => ({ ...event, enabled: choice[event.id] })));
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Не удалось сохранить настройки уведомлений'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => applyEvents(events);

  // Ошибку подписки показывает Alert ниже из состояния хука; здесь только успех и запреты браузера
  const handlePushToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.checked) {
      if (await unsubscribe()) notify.info('Браузерные уведомления на этом устройстве отключены');
      return;
    }
    if (!isSupported) {
      notify.error('Браузер не поддерживает Web Push уведомления');
      return;
    }
    if (permission === 'denied') {
      notify.warning('Уведомления заблокированы в настройках браузера');
      return;
    }
    if (await subscribe()) notify.success('Браузерные уведомления на этом устройстве включены');
  };

  const pushBadge = isSubscribed ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <CheckCircle sx={{ fontSize: 14, color: ZERDE_COLORS.emerald }} />
      {statusText('Push включен', '#047857')}
    </Box>
  ) : !isSupported ? (
    statusText('Push не поддерживается', '#64748b')
  ) : permission === 'denied' ? (
    statusText('Push заблокирован в браузере', '#ef4444')
  ) : (
    statusText('Push отключен', '#94a3b8')
  );

  // Раздел - про браузерные уведомления: пока подписки на этом устройстве нет, события выключены и заблокированы
  const locked = !isSubscribed;

  // Канал: браузерные push-уведомления на этом устройстве
  const pushBlock = (
    <Box
      sx={{
        p: 2,
        borderRadius: '12px',
        bgcolor: isSubscribed ? '#ffffff' : '#f8fafc',
        border: `1px solid ${isSubscribed ? 'rgba(86, 191, 132, 0.45)' : '#e2e8f0'}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem', lineHeight: 1.3 }}>
            Браузерные уведомления на этом устройстве
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, fontSize: '0.85rem' }}>
            Приходят, даже когда вкладка портала свернута. Подписка действует только в этом браузере
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          {isLoading && <CircularProgress size={20} sx={{ color: ZERDE_COLORS.primary }} />}
          <Switch
            checked={isSubscribed}
            onChange={handlePushToggle}
            disabled={isLoading || !isSupported}
            inputProps={{ 'aria-label': 'Браузерные уведомления на этом устройстве' }}
            sx={switchSx}
          />
        </Box>
      </Box>

      {!isSupported && (
        <Alert severity="info" sx={{ borderRadius: '12px' }}>
          Браузер не поддерживает Web Push. Подойдет актуальная версия Google Chrome, Microsoft Edge или Mozilla Firefox.
        </Alert>
      )}

      {isSupported && permission === 'denied' && (
        <Alert severity="warning" sx={{ borderRadius: '12px', border: '1px solid #fed7aa', bgcolor: '#fff7ed', color: '#9a3412' }}>
          Уведомления заблокированы в настройках браузера. Нажмите на значок настроек сайта слева от адресной строки и
          разрешите «Уведомления».
        </Alert>
      )}

      {error && permission !== 'denied' && (
        <Alert severity="error" sx={{ borderRadius: '12px' }}>
          {error}
        </Alert>
      )}
    </Box>
  );

  return (
    <Paper
      id="section-notifications"
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        borderRadius: '20px',
        bgcolor: '#ffffff',
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.03)',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        scrollMarginTop: '24px'
      }}
    >
      <SectionHeaderDivider
        id="header-notifications"
        icon={<NotificationsNoneOutlined sx={{ fontSize: 22 }} />}
        title="Настройка уведомлений"
        subtitle="Браузерные уведомления на этом устройстве и события, о которых они приходят"
        badge={pushBadge}
      />

      {pushBlock}

      {/* События: без браузерной подписки уведомления не придут, поэтому выбор заблокирован и показан выключенным */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>События</Typography>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
            {locked && <LockOutlined sx={{ fontSize: 14 }} />}
            {locked
              ? 'Станут доступны после включения браузерных уведомлений'
              : 'О чем присылать уведомления. Выбор одинаков на всех ваших устройствах'}
          </Typography>
        </Box>

        {loadError && (
          <Alert
            severity="error"
            sx={{ borderRadius: '12px' }}
            action={
              <Button color="inherit" size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 600 }}>
                Повторить
              </Button>
            }
          >
            {loadError}
          </Alert>
        )}

        {loading && events.length === 0 && !loadError ? (
          <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={26} />
          </Box>
        ) : (
          <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', bgcolor: locked ? '#f8fafc' : '#ffffff' }}>
            {events.map((event, index) => (
              <Box
                key={event.id}
                sx={{
                  px: 2,
                  py: 1.25,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  borderTop: index === 0 ? 'none' : '1px solid #f1f5f9',
                  '&:hover': locked ? undefined : { bgcolor: '#f8fafc' }
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: locked ? '#94a3b8' : '#1e293b', fontSize: '0.9rem' }}>
                    {event.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: locked ? '#cbd5e1' : '#64748b', fontSize: '0.8rem', display: 'block', mt: 0.25 }}>
                    {event.description}
                  </Typography>
                </Box>
                <Switch
                  checked={!locked && Boolean(choice[event.id])}
                  onChange={e => setChoice(prev => ({ ...prev, [event.id]: e.target.checked }))}
                  disabled={locked || saving}
                  inputProps={{ 'aria-label': event.title }}
                  sx={{ ...switchSx, flexShrink: 0 }}
                />
              </Box>
            ))}
          </Box>
        )}

        {!locked && events.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {dirty && (
              <Typography variant="caption" sx={{ color: '#b45309', fontWeight: 600, mr: 'auto' }}>
                Есть несохраненные изменения
              </Typography>
            )}
            {dirty && (
              <Button
                size="small"
                onClick={handleReset}
                disabled={saving}
                sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b', borderRadius: '8px' }}
              >
                Отменить изменения
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              disableElevation
              onClick={handleSave}
              disabled={!dirty || saving}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', bgcolor: ZERDE_COLORS.primary, px: 2 }}
            >
              Сохранить
            </Button>
          </Box>
        )}
      </Box>

      <Box>
        <Button
          startIcon={<InfoOutlined sx={{ fontSize: 16 }} />}
          onClick={() => setInfoExpanded(!infoExpanded)}
          size="small"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: '#64748b',
            p: 0,
            minWidth: 0,
            fontSize: '0.8rem',
            '&:hover': { bgcolor: 'transparent', color: ZERDE_COLORS.primary }
          }}
        >
          {infoExpanded ? 'Скрыть информацию' : 'Как это работает'}
        </Button>
        <Collapse in={infoExpanded}>
          <Box sx={{ mt: 1.5, p: 2, borderRadius: '12px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6, fontSize: '0.82rem' }}>
              • Браузерная подписка оформляется отдельно на каждом устройстве и в каждом браузере.
              <br />
              • Выбор событий хранится в вашей учетной записи и одинаков на всех устройствах.
              <br />
              • Сейчас браузерные уведомления приходят по документам аптеки; учет выбранных событий в рассылках
              подключается поэтапно.
            </Typography>
          </Box>
        </Collapse>
      </Box>
    </Paper>
  );
};

export default NotificationSettingsSection;
